import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogHeader } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Bot, Plus, Trash2, Briefcase, Sparkles, LineChart, Zap, Rocket, Users, Wand2 } from "lucide-react";
import { toast } from "sonner";

const ICON_MAP = {
  briefcase: Briefcase,
  sparkles: Sparkles,
  "line-chart": LineChart,
  zap: Zap,
  rocket: Rocket,
  users: Users,
};

const COLOR_MAP = {
  sky: "bg-sky-500/10 text-sky-300 border-sky-400/20",
  emerald: "bg-emerald-500/10 text-emerald-300 border-emerald-400/20",
  violet: "bg-violet-500/10 text-violet-300 border-violet-400/20",
  amber: "bg-amber-500/10 text-amber-300 border-amber-400/20",
  rose: "bg-rose-500/10 text-rose-300 border-rose-400/20",
  cyan: "bg-cyan-500/10 text-cyan-300 border-cyan-400/20",
};

export default function Agents() {
  const [agents, setAgents] = useState([]);
  const [presets, setPresets] = useState([]);
  const [deploying, setDeploying] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", persona: "Consultative", tone: "Friendly", icp: "", value_prop: "" });

  const load = () => api.get("/agents").then((r) => setAgents(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/agents/presets").then((r) => setPresets(r.data.presets)).catch(() => {});
  }, []);

  const create = async () => {
    if (!form.name || !form.icp || !form.value_prop) return toast.error("Fill name, ICP and value prop");
    try {
      await api.post("/agents", form);
      toast.success("Agent deployed");
      setOpen(false);
      setForm({ name: "", persona: "Consultative", tone: "Friendly", icp: "", value_prop: "" });
      load();
    } catch (e) {
      toast.error("Could not create agent");
    }
  };

  const deployPreset = async (p) => {
    setDeploying(p.preset_id);
    try {
      await api.post("/agents/deploy-preset", { preset_id: p.preset_id });
      toast.success(`${p.name} deployed`);
      load();
    } catch {
      toast.error("Could not deploy");
    } finally {
      setDeploying(null);
    }
  };

  const remove = async (id) => {
    await api.delete(`/agents/${id}`);
    toast.success("Agent removed");
    load();
  };

  return (
    <div className="space-y-8" data-testid="agents-view">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">AI BDR agents</h1>
          <p className="mt-1 text-sm text-slate-400">Deploy from the library or craft a custom persona.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="agents-create-btn" className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold rounded-full">
              <Plus className="w-4 h-4 mr-1" /> Custom agent
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B1020] border-white/10 text-slate-100 max-w-lg">
            <DialogHeader><DialogTitle>Deploy a new AI BDR</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Agent name</Label><Input data-testid="agent-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Ava — Enterprise" className="bg-white/[0.03] border-white/10 mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Persona</Label>
                  <Select value={form.persona} onValueChange={(v) => setForm({ ...form, persona: v })}>
                    <SelectTrigger data-testid="agent-form-persona" className="bg-white/[0.03] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Consultative", "Challenger", "Analyst", "Storyteller"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tone</Label>
                  <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
                    <SelectTrigger data-testid="agent-form-tone" className="bg-white/[0.03] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Friendly", "Direct", "Playful", "Executive"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Ideal Customer Profile</Label><Textarea data-testid="agent-form-icp" value={form.icp} onChange={(e) => setForm({ ...form, icp: e.target.value })} placeholder="B2B SaaS · 50–500 emp · Series B+" className="bg-white/[0.03] border-white/10 mt-1 min-h-[70px]" /></div>
              <div><Label>Value proposition</Label><Textarea data-testid="agent-form-value" value={form.value_prop} onChange={(e) => setForm({ ...form, value_prop: e.target.value })} placeholder="We book 3–5× more meetings via autonomous 7-touch outbound" className="bg-white/[0.03] border-white/10 mt-1 min-h-[70px]" /></div>
              <Button onClick={create} data-testid="agent-form-submit" className="w-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold">Deploy agent</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Preset library */}
      <section data-testid="agents-preset-library">
        <div className="flex items-center gap-2 mb-3">
          <Wand2 className="w-4 h-4 text-amber-300" />
          <div className="text-sm font-semibold">Preset library · one-click deploy</div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {presets.map((p) => {
            const Icon = ICON_MAP[p.icon] || Bot;
            const color = COLOR_MAP[p.color] || COLOR_MAP.sky;
            return (
              <div key={p.preset_id} data-testid={`preset-card-${p.preset_id}`} className={`rounded-2xl border ${color.split(" ").filter((c) => c.startsWith("border")).join(" ")} bg-white/[0.02] p-5 hover:bg-white/[0.04] transition`}>
                <div className={`w-10 h-10 rounded-xl grid place-items-center ${color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="mt-4">
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{p.persona} · {p.tone}</div>
                </div>
                <div className="mt-3 text-xs text-slate-300 leading-relaxed line-clamp-2">{p.tagline}</div>
                <Button
                  onClick={() => deployPreset(p)}
                  disabled={deploying === p.preset_id}
                  data-testid={`preset-deploy-${p.preset_id}`}
                  size="sm"
                  className="mt-4 w-full bg-white/[0.05] hover:bg-white/[0.10] border border-white/10 text-slate-100"
                >
                  {deploying === p.preset_id ? "Deploying…" : "Deploy"}
                </Button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Deployed agents */}
      <section>
        <div className="text-sm font-semibold mb-3">Your deployed agents</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-white/10 p-10 text-center text-slate-500" data-testid="agents-empty-state">
              <Bot className="w-10 h-10 mx-auto text-slate-600 mb-3" />
              Deploy a preset above or create a custom agent to start.
            </div>
          )}
          {agents.map((a) => (
            <div key={a.agent_id} className="rounded-2xl border border-white/5 bg-white/[0.02] p-5 group" data-testid={`agent-card-${a.agent_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-sky-500/10 grid place-items-center text-sky-300">
                    <Bot className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold">{a.name}</div>
                    <div className="text-xs text-slate-500">{a.persona} · {a.tone}</div>
                  </div>
                </div>
                <button onClick={() => remove(a.agent_id)} data-testid={`agent-delete-${a.agent_id}`} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-400 transition">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-4 text-xs text-slate-400 line-clamp-2">{a.icp}</div>
              <div className="mt-4 flex items-center justify-between">
                <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-400/20">● {a.status}</Badge>
                <div className="text-xs text-slate-500 font-mono">{a.stats?.sent || 0} sent · {a.stats?.meetings || 0} mtgs</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
